import React, { useState } from "react";
import { ArrowDown, ExternalLink } from "lucide-react";

const THEMES = [
  {
    id: "consumer-duty",
    name: "Consumer Duty",
    question: "How do we identify recurring risks across AI-led customer conversations?",
    control: "Screen configured interaction types, surface designated risk indicators and provide reviewable incidents and management information.",
    evidence: "Interactions, risk categories, actions, reviews, outcomes and trends.",
  },
  {
    id: "governance",
    name: "Governance & Controls",
    question: "Are AI-related controls defined, operated and reviewed?",
    control: "Configurable, versioned policies with runtime enforcement and an audit trail.",
    evidence: "Policy versions, triggers and the actions taken.",
  },
  {
    id: "smcr",
    name: "SM&CR / Accountability",
    question: "Can we show who was accountable for a decision involving AI?",
    control: "Final decisions and escalations stay with named people; norse3 does not decide for the firm.",
    evidence: "Attributable, time-stamped reviewer decisions.",
  },
  {
    id: "vulnerable",
    name: "Vulnerable Customers",
    question: "How do we surface interactions that may need additional support?",
    control: "Detect potential vulnerability indicators and route to your defined support procedure.",
    evidence: "Indicator, policy applied, human decision and outcome.",
  },
  {
    id: "resilience",
    name: "Operational Resilience",
    question: "What happens to AI-led journeys if a control is unavailable?",
    control: "Agreed fail-open / fail-closed behaviour, monitoring and fallback procedures per workflow.",
    evidence: "Configured behaviour and monitoring records.",
  },
  {
    id: "data",
    name: "Data Protection",
    question: "How is customer information handled by AI systems?",
    control: "Detect and act on sensitive content; minimise stored data and retain a reference where configured.",
    evidence: "Minimal evidence reference and handling records.",
  },
  {
    id: "ai-gov",
    name: "AI Governance",
    question: "How do we maintain central visibility of AI use and risk?",
    control: "One console for policy, incidents, review and management information across the estate.",
    evidence: "Incidents, escalations and management information.",
  },
];

const SOURCES = [
  { label: "FCA", href: "https://www.fca.org.uk/firms/artificial-intelligence-ai" },
  { label: "FCA Handbook", href: "https://www.handbook.fca.org.uk/" },
  { label: "Bank of England", href: "https://www.bankofengland.co.uk/" },
  { label: "PRA", href: "https://www.bankofengland.co.uk/prudential-regulation" },
  { label: "ICO", href: "https://ico.org.uk/" },
];

const LABELS = ["Regulatory theme", "Operational question", "Possible norse3 control", "Evidence"];

export default function RegulatoryReality() {
  const [active, setActive] = useState(THEMES[0].id);
  const cur = THEMES.find((t) => t.id === active);
  const rows = [cur.name, cur.question, cur.control, cur.evidence];

  return (
    <div data-testid="fs-regulatory">
      <div className="flex flex-wrap gap-2">
        {THEMES.map((t) => {
          const on = t.id === active;
          return (
            <button key={t.id} onClick={() => setActive(t.id)} data-testid={`fs-reg-${t.id}`}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${on ? "border-primary/40 bg-primary/[0.08] text-foreground" : "border-white/12 bg-navy-900/60 text-muted-foreground hover:border-white/25"}`}>
              {t.name}
            </button>
          );
        })}
      </div>

      <div className="mt-5 rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-6">
        <div className="grid gap-2.5">
          {rows.map((r, ri) => (
            <div key={ri}>
              <div className={`rounded-xl border px-4 py-3 ${ri === 2 ? "border-primary/30 bg-primary/[0.05]" : ri === 3 ? "border-signal-green/25 bg-signal-green/[0.04]" : "border-white/10 bg-navy-800/50"}`}>
                <p className={`text-[0.65rem] font-semibold uppercase tracking-[0.14em] ${ri === 2 ? "text-primary" : ri === 3 ? "text-signal-green" : "text-muted-foreground/70"}`}>{LABELS[ri]}</p>
                <p className="mt-1 text-sm text-foreground/90 leading-relaxed">{r}</p>
              </div>
              {ri < rows.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-navy-900/60 px-5 py-4">
        <p className="text-sm text-muted-foreground">Using norse3 does not satisfy any regulatory requirement. Firms remain responsible for determining their obligations and whether they are meeting them. Authoritative sources:</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {SOURCES.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-navy-800/60 px-3 py-1.5 text-xs text-primary hover:bg-white/5">
              {s.label} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
